import { Link, Head } from "@inertiajs/react"
import { Radar, BarChart3, Wallet, FileText, Building2, ArrowRight } from "lucide-react"

const SECTIONS = [
  { icon: BarChart3, name: "Overview", blurb: "True Sales, cash, and utilisation at a glance." },
  { icon: Wallet, name: "Cash Collections", blurb: "What actually landed in the bank each month, net of fees and FX." },
  { icon: FileText, name: "Sales Report", blurb: "True Sales, settlement by payment, and the 17-month revenue trend." },
  { icon: Building2, name: "DSD Center", blurb: "Planning Center volume, invoicing, and credit utilisation." },
]

export default function Home() {
  const description =
    "SONAR is Digital Smile Design's internal financial reporting: True Sales, cash collections, and Planning Center performance in one reconciled source of truth."

  return (
    <>
      <Head title="SONAR · DSD Financial Reporting">
        <meta name="description" content={description} />
        <meta property="og:title" content="SONAR · DSD Financial Reporting" />
        <meta
          property="og:description"
          content="Digital Smile Design's single, reconciled view of how the business earns and collects."
        />
      </Head>

      <div className="sonar-app min-h-screen bg-stone-50 text-stone-900 dark:bg-stone-950 dark:text-stone-100">
        <main className="mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-10">
          {/* Brand mark */}
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-stone-900 text-champagne-400 dark:bg-champagne-500 dark:text-stone-900">
              <Radar className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-base font-semibold tracking-tight">SONAR</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400 dark:text-stone-500">
                Financial Reporting
              </span>
            </span>
          </div>

          {/* Hero */}
          <div className="flex flex-1 flex-col justify-center py-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-champagne-700 dark:text-champagne-400">
              Digital Smile Design · Internal
            </p>
            <h1 className="mt-3 max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              How DSD earns and collects, in one reconciled view.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-500 dark:text-stone-400">
              SONAR brings True Sales, cash collections, and Planning Center performance into a single
              source of truth. Currencies kept separate, the lenses kept honest, and every figure tied
              back to the books.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                href="/login"
                className="inline-flex items-center rounded-lg bg-stone-900 px-5 py-2.5 no-underline transition-colors hover:bg-stone-800 dark:bg-champagne-500 dark:hover:bg-champagne-400"
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium text-white dark:text-stone-900">
                  Log in
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
              <span className="text-sm text-stone-400 dark:text-stone-500">
                Access is invite-only. Ask your admin to be added.
              </span>
            </div>
          </div>

          {/* What's inside */}
          <div className="grid gap-4 border-t border-stone-200 pt-10 dark:border-stone-800 sm:grid-cols-2 lg:grid-cols-4">
            {SECTIONS.map((s) => (
              <div
                key={s.name}
                className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900"
              >
                <s.icon className="h-5 w-5 text-champagne-600 dark:text-champagne-400" strokeWidth={2} />
                <h3 className="mt-3 text-sm font-semibold text-stone-800 dark:text-stone-100">{s.name}</h3>
                <p className="mt-1 text-xs leading-relaxed text-stone-500 dark:text-stone-400">{s.blurb}</p>
              </div>
            ))}
          </div>

          <footer className="mt-10 text-xs text-stone-400 dark:text-stone-600">
            Digital Smile Design · SONAR · Internal financial reporting
          </footer>
        </main>
      </div>
    </>
  )
}
